// Full-screen panel over the product page that hosts the owner's slot
// calendar screens (Setup / Manage). Same route as the listing - see
// useSlotCalendarPanel - so the product stays loaded underneath (no refetch,
// no route change) and back simply closes it. Both screens stay lazy, so
// customers never download them.
import React, { Suspense, lazy, useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import useScrollLock from "../../../../../../hooks/useScrollLock";
import { useSlotCalendarPanel } from "../../hooks/useSlotCalendarPanel";
import { useSlotCalendarGate } from "../../hooks/useSlotCalendarViewer";
import "./styles/SlotCalendarPanelHost.css";

const SlotCalendarManagePage = lazy(() => import("./manage/SlotCalendarManagePage"));
const SlotCalendarSetupPage = lazy(() => import("./setup/SlotCalendarSetupPage"));

const SlotCalendarPanelHost: React.FC = () => {
  const { panel, open, close } = useSlotCalendarPanel();
  const featureOn = useSlotCalendarGate();
  const isOpen = panel !== null && featureOn;

  useScrollLock(isOpen);

  // A panel URL for a listing where the feature is off: drop it quietly.
  useEffect(() => {
    if (panel !== null && !featureOn) close();
  }, [panel, featureOn, close]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="scp-panel"
      role="dialog"
      aria-modal="true"
      aria-label={panel === "setup" ? "Turn on availability" : "Manage availability"}
    >
      <Suspense
        fallback={
          <div className="scp-panel__loading">
            <Loader2 className="scp-panel__spin" size={20} /> Loading...
          </div>
        }
      >
        {panel === "setup" ? (
          <SlotCalendarSetupPage onClose={close} onDone={() => open("manage")} />
        ) : (
          <SlotCalendarManagePage onClose={close} onOpenSetup={() => open("setup")} />
        )}
      </Suspense>
    </div>,
    document.body
  );
};

export default SlotCalendarPanelHost;
